/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders, Axios } from 'axios'
import {AuthModel} from './_models'
import { setupCache } from 'axios-cache-interceptor';
import { doLogout, getUserRoleAccess } from '../../../api';
import { jwtDecode } from "jwt-decode";
import { logoutFunction } from './Auth';
import { recordAxiosTiming, startApiTimer } from '../../utils/ApiTimingLogger';
import { noteApiActivity, waitForWarmup } from '../../../warmup';

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuthentication = (): AuthModel | undefined => {

  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      const tokenExpired = isTokenExpired(auth.api_token)
      if(tokenExpired){
        removeAuth()
         // triggerExternalLogout()
      }
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

export const isTokenExpired = (token?: string): boolean => {
  try {
      const decoded: any = jwtDecode(token||'');
      
      if (!decoded.exp) {
          return false; // No expiration field, assume the token does not expire
      }
      const currentTime = Date.now() / 1000; // Current time in seconds
      const tokenExpiry = decoded.exp < currentTime
      return tokenExpiry ; // Return true if expired
  } catch (error) {
      console.error('Failed to decode token', error);
      return true; // Consider token expired if there's an error
  }
};

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}


const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

let _loggingOut = false;
export const markLoggingOut = () => {
  _loggingOut = true;
  // Immediately clear the stale axios default Authorization header
  delete (axios.defaults.headers.common as any)['Authorization'];
};
export const clearLoggingOut = () => { _loggingOut = false };
export const isLoggingOut = () => _loggingOut;

const I18N_CONFIG_KEY = import.meta.env.VITE_APP_I18N_CONFIG_KEY || 'i18nConfig';

function getStoredLang(): string {
  try {
    const raw = localStorage.getItem(I18N_CONFIG_KEY);
    if (raw) return JSON.parse(raw).selectedLang || 'en';
  } catch {}
  return 'en';
}

export function setupAxios(axiosInstance: AxiosInstance) {
  axiosInstance.defaults.baseURL = import.meta.env.VITE_APP_API_BASE_URL;
  axiosInstance.defaults.headers.Accept = 'application/json';
  // API response-time logging. Registered first so it runs right after the cache interceptor
  // (which flags cache hits) and before the date conversion below, so the time excludes client-side parsing.
  axiosInstance.interceptors.response.use((response) => {
    recordAxiosTiming(response.config, response.status, response);
    if (!(response as any).cached) noteApiActivity();
    return response;
  }, (error) => {
    if (!axios.isCancel(error)) {
      recordAxiosTiming(error.config, error.response?.status ?? 'ERR', error.response);
      if (error.response) noteApiActivity();
    }
    return Promise.reject(error);
  });
  axiosInstance.interceptors.response.use((response) => {
    const convertDates = (obj: any): any => {
      if (obj instanceof Blob) {
        // If the object is a Blob, return it without modification
        return obj;
      }

      if (typeof obj === 'string' && isoDateFormat.test(obj)) {
        return new Date(obj);
      } else if (obj !== null && typeof obj === 'object') {
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            obj[key] = convertDates(obj[key]);
          }
        }
      }
      return obj;
    };


    response.data = convertDates(response.data);
    return response;
  }, (error) => {
    if (error.response?.status === 403 && !_loggingOut) {
      const url: string = error.config?.url ?? '';
      const isSilent: boolean = (error.config as any)?.__silent === true;
      // Don't show upgrade modal for:
      // - auth / subscription management endpoints
      // - requests explicitly marked as silent (background preloads)
      if (!isSilent && !url.startsWith('/auth') && !url.startsWith('/subscription') && !url.includes('getBranchesForUser')) {
        const message: string = error.response?.data?.message ?? '';
        window.dispatchEvent(
          new CustomEvent('subscription:upgrade-required', { detail: { message } })
        );
      }
    }
    if (error.response?.status === 401 && !_loggingOut) {
      const url: string = error.config?.url ?? '';
      // A 401 mid-session means the token was invalidated server-side (password
      // change, revoked session, clock skew) after already passing the client-side
      // expiry check on the way out. Run the same logout flow as an expired token
      // instead of leaving every caller to handle a raw, uncaught 401.
      if (!url.startsWith('/auth')) {
        logoutFunction(true);
        return Promise.reject(new axios.Cancel('Session expired'));
      }
    }
    return Promise.reject(error);
  });
  // Request interceptor to check cache and ETag
  axiosInstance.interceptors.request.use(
    
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      await waitForWarmup();
      // Block non-auth API calls immediately when logout is in progress.
      // Allow /auth/* through so the login form still works.
      if (_loggingOut && !config.url?.startsWith('/auth')) {
        return Promise.reject(new axios.Cancel('Logging out')) as any;
      }

      const auth = getAuthentication();
      
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }
      config.headers.set('Accept-Language', getStoredLang());

      if (auth && auth.api_token) {
        if (isTokenExpired(auth.api_token)) {
          removeAuth()
          window.location.replace('/auth/login')
          return Promise.reject(new axios.Cancel('Session expired')) as any
        }
        config.headers.set('Authorization', `Bearer ${auth.api_token}`);
      }

      const url = config.url as string;
      const cachedData = await getCachedData(url);
      if (cachedData) {
        config.headers.set('If-None-Match', cachedData.etag);
      }

      return config;
    },
    (err: AxiosError) => Promise.reject(err)
  );

  // Registered after the interceptor above, so it runs first (request interceptors run last-in-first-out)
  // and the timer covers that interceptor's work too.
  axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    startApiTimer(config);
    return config;
  });

  // Response interceptor to handle caching
  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse): Promise<AxiosResponse> => {
      
      const url = response.config.url as string;
      const etag = response.headers['etag'] || response.headers['ETag'];
      if (etag) {
        const isBlob = response.config.responseType === 'blob';
        await cacheData(url, response.data, etag, isBlob);
      }
      return response;
    },
    async (error: AxiosError) => {
      if (error.response && error.response.status === 304 && error.config) {
        // Handle 304 Not Modified response
        const url = error.config.url as string;
        const cachedData = await getCachedData(url);
        if (cachedData) {
          return Promise.resolve({ data: cachedData.data, status: 200, statusText: 'OK', headers: error.response.headers, config: error.config, request: error.request });
        }
      }
      return Promise.reject(error);
    }
  );
}


const cacheData = async (url: string, data: any, etag: string, isBlob: boolean): Promise<void> => {
  const cache = await caches.open('file-cache');
  let response: Response;

  if (isBlob) {
    response = new Response(data, {
      headers: { 'Content-Type': data.type }
    });
  } else {
    response = new Response(JSON.stringify(data), {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  await cache.put(url, response);
  localStorage.setItem(`${url}-etag`, etag);
};

const getCachedData = async (url: string): Promise<{ data: any; etag: string } | null> => {
  const cache = await caches.open('file-cache');
  const response = await cache.match(url);
  const etag = localStorage.getItem(`${url}-etag`);

  if (response && etag) {
    const contentType = response.headers.get('Content-Type');
    
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.blob();
    }

    return { data, etag };
  }
  return null;
};

//Active Usage
export const clearCachedData = async ()=>{
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);

    // Check if the key starts with 'axios-cache:'
    if (key && key.startsWith('axios-cache:')) {
        localStorage.removeItem(key);
        // Adjust the index after deletion since localStorage length will decrease
        i--;
    }
}
// await doLogout();
// delete axios.defaults.headers.common['Authorization'];
}

export function updateAxiosToken(token: string) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export {getAuthentication as getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}

import { LoginInfo } from '../../../types';

export const collectLoginInfo = (): LoginInfo => {
  const ua = navigator.userAgent;

  // Device type
  const isMobile = /iPhone|Android.*Mobile|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  // OS
  let osName = 'Unknown';
  if (/Windows NT 10/.test(ua))        osName = 'Windows 10';
  else if (/Windows NT 6\.3/.test(ua)) osName = 'Windows 8.1';
  else if (/Windows NT 6\.1/.test(ua)) osName = 'Windows 7';
  else if (/Windows/.test(ua))         osName = 'Windows';
  else if (/iPhone OS ([0-9_]+)/.test(ua)) {
    osName = `iOS ${ua.match(/iPhone OS ([0-9_]+)/)?.[1].replace(/_/g, '.')}`;
  } else if (/iPad.*OS ([0-9_]+)/.test(ua)) {
    osName = `iPadOS ${ua.match(/OS ([0-9_]+)/)?.[1].replace(/_/g, '.')}`;
  } else if (/Android ([0-9.]+)/.test(ua)) {
    osName = `Android ${ua.match(/Android ([0-9.]+)/)?.[1]}`;
  } else if (/Mac OS X ([0-9_]+)/.test(ua)) {
    osName = `macOS ${ua.match(/Mac OS X ([0-9_]+)/)?.[1].replace(/_/g, '.')}`;
  } else if (/Linux/.test(ua)) {
    osName = 'Linux';
  }

  // Browser (order matters: Edge before Chrome, Opera before Chrome)
  let browser = 'Unknown';
  if (/Edg\/([0-9]+)/.test(ua)) {
    browser = `Edge ${ua.match(/Edg\/([0-9]+)/)?.[1]}`;
  } else if (/OPR\/([0-9]+)/.test(ua)) {
    browser = `Opera ${ua.match(/OPR\/([0-9]+)/)?.[1]}`;
  } else if (/Chrome\/([0-9]+)/.test(ua)) {
    browser = `Chrome ${ua.match(/Chrome\/([0-9]+)/)?.[1]}`;
  } else if (/Firefox\/([0-9]+)/.test(ua)) {
    browser = `Firefox ${ua.match(/Firefox\/([0-9]+)/)?.[1]}`;
  } else if (/Safari\//.test(ua)) {
    browser = 'Safari';
  }

  // Device name (best-effort for mobile)
  let deviceName: string | undefined;
  if (/iPhone/.test(ua)) deviceName = 'iPhone';
  else if (/iPad/.test(ua)) deviceName = 'iPad';
  else if (/Android/.test(ua)) {
    deviceName = ua.match(/\(Linux; Android [0-9.]+; ([^)]+)\)/)?.[1];
  }

  // Location hint via IANA timezone (e.g. "Asia/Kolkata")
  const location = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return { deviceType, osName, browser, deviceName, location };
};
